import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Media,
  MediaPickerComponent,
  PickerMode,
  Product,
  useUpdateProduct,
} from '@gripp/shared-logic';
import React, { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import LoadingIndicator from '../loadingIndicator';
import { ImageData, useTypedMediaItem } from '../media';

type ChangeProductImageProps = {
  product: Product;
  mediaPicker: MediaPickerComponent;
  onClose: () => void;
  onImageChanged?: () => void;
  currentFormData?: {
    name?: string;
    description?: string;
    details?: string;
    specifications?: string;
  };
  /** [optional] Custom trigger element for opening the media picker */
  customTrigger?: ReactNode;
};

export const ChangeProductImage: React.FC<ChangeProductImageProps> = (props) => {
  const { t } = useTranslation();
  const MediaPicker = props.mediaPicker;
  const { handleSet, handleUploadProgress } =
    useTypedMediaItem<ImageData>(undefined);
  const [uploading, setUploading] = useState(false);

  const { updateProduct } = useUpdateProduct({
    productId: props.product.id,
  });

  const setCoverImage = (image: Media) => {
    onSubmit(image.id);
  };

  const onSubmit = async (imageId: string) => {
    const input = {
      id: props.product.id,
      name: props.currentFormData?.name ?? props.product.name,
      description: props.currentFormData?.description ?? props.product.description,
      details: props.currentFormData?.details ?? props.product.details,
      specifications: props.currentFormData?.specifications ?? props.product.specifications,
      coverImage: { id: imageId },
    };

    await updateProduct(
      {
        input: input,
      },
      {
        onSettled: async () => {
          props?.onImageChanged?.();
          props?.onClose?.();
        },
      }
    );
  };

  return (
    <View style={styles.buttonContainer}>
      <MediaPicker
        mode={PickerMode.CAMERA}
        setMedia={(media: ImageData) => handleSet(media, setCoverImage)}
        setProgress={handleUploadProgress}
        setUploading={setUploading}
        customTrigger={
          props.customTrigger ? (
            <>{props.customTrigger}</>
          ) : (
            <View style={styles.buttonChange}>
              <View style={styles.container}>
                {uploading ? (
                  <View style={styles.loadingContainer}>
                    <LoadingIndicator size={20} />
                  </View>
                ) : (
                  <>
                    <MaterialCommunityIcons
                      name="autorenew"
                      color={Colors.primaryDark}
                      size={20}
                      style={{ paddingRight: 6 }}
                    />
                    <Text style={styles.labelButtonChange}>
                      {t('product.change')}
                    </Text>
                  </>
                )}
              </View>
            </View>
          )
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    width: '100%',
  },
  buttonChange: {
    alignSelf: 'center',
    zIndex: 9,
    position: 'absolute',
    backgroundColor: Colors.white,
    color: Colors.primaryDark,
    borderRadius: 8,
    borderColor: Colors.primaryDark,
    borderWidth: 2,
    width: 145,
    height: 56,
    ...Platform.select({
      web: {
        bottom: 40,
        height: 56,
      },
      default: {
        justifyContent: 'center',
        bottom: 35,
        left: 40,
      },
    }),
  },
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,

    ...Platform.select({
      web: {
        bottom: 4,
        height: 56,
      },
      default: {
        zIndex: 9,
      },
    }),
  },
  loadingContainer: {
    backgroundColor: Colors.white,
    margin: 'auto',
  },
  labelButtonChange: {
    color: Colors.primaryDark,
    fontSize: 20,
    fontWeight: 500,
  },
});
